import { Controller } from '@hotwired/stimulus';
import { field, message, paint, role } from '../lib/dom';
import { callableWorkflow, stepDetail, stepTitle, triggerFace } from '../lib/workflow/presenter';

export default class extends Controller {
  static targets = [
    'fieldsTemplate', 'rowTemplate', 'stepBundleTemplate', 'stepMcpServerTemplate', 'stepsList',
    'triggerFieldsTemplate', 'triggerRowTemplate', 'triggersList',
  ];

  static classes = ['modeActive', 'modeIdle', 'rowFaulty', 'rowIdle', 'rowOpen'];

  render(scene) {
    this.scene = scene;

    this.#renderTriggers();
    this.#renderSteps();
  }

  revealConditions(id) {
    const row = this.stepsListTarget.querySelector(`[data-step-id="${id}"]`);

    row?.querySelector('[data-role="conditions-field"]')?.scrollIntoView({ block: 'center', behavior: 'smooth' });
  }

  changeTrigger({ currentTarget }) {
    const row = currentTarget.closest('[data-trigger-id]');
    const trigger = this.#trigger(row.dataset.triggerId);

    trigger.name = field(row, 'name').value;
    trigger.webhookId = field(row, 'webhook').value;
    trigger.events = field(row, 'events').value;
    trigger.conditions = field(row, 'conditions').value;
    trigger.cron = field(row, 'cron').value;

    this.#renderTriggerRowHeader(row, trigger);
    this.#announce(`${trigger.id}:${currentTarget.dataset.field}`, { canvas: true });
  }

  changeStep({ currentTarget }) {
    const row = currentTarget.closest('[data-step-id]');
    const step = this.#step(row.dataset.stepId);

    step.name = field(row, 'name').value;
    step.key = field(row, 'key').value;
    step.command = field(row, 'command').value;
    step.credentialId = field(row, 'credential_id').value;
    step.provider = this.#providerOf(step.credentialId);
    step.prompt = field(row, 'prompt').value;
    step.conditions = field(row, 'conditions').value;
    step.instructions = field(row, 'instructions').value;
    step.interruptible = field(row, 'interruptible').checked;
    step.childWorkflowId = field(row, 'child_workflow_id').value;
    step.childInputs = field(row, 'child_inputs').value;
    step.each = field(row, 'each').value;
    step.allowFailures = field(row, 'allow_failures').checked;

    this.#renderStepMcpServers(row, step);
    this.#renderChildInputsDeclared(row, step);
    this.#renderRowHeader(row, step);
    this.#announce(`${step.id}:${currentTarget.dataset.field}`, { canvas: true });
  }

  changeMode({ currentTarget, params: { mode } }) {
    const row = currentTarget.closest('[data-step-id]');
    const step = this.#step(row.dataset.stepId);

    step.mode = mode;

    this.#renderMode(row, step);
    this.#renderRowHeader(row, step);
    this.#announce(null, { canvas: true });
  }

  addStepBundle({ currentTarget }) {
    const row = currentTarget.closest('[data-step-id]');
    const step = this.#step(row.dataset.stepId);

    step.bundles.push({ bundleId: '', revisionNumber: null });

    this.#renderStepBundles(row, step);
    this.#announce();
  }

  changeStepBundle({ currentTarget }) {
    const row = currentTarget.closest('[data-step-id]');
    const step = this.#step(row.dataset.stepId);

    step.bundles = this.#bundleRows(row).map((node, index) => {
      const bundleId = field(node, 'bundle').value;
      const changed = bundleId !== step.bundles[index]?.bundleId;
      const revision = field(node, 'revision').value;

      return { bundleId: bundleId, revisionNumber: changed || !revision ? null : Number(revision) };
    });

    this.#renderStepBundles(row, step);
    this.#renderRowHeader(row, step);
    this.#announce(null, { canvas: true });
  }

  deleteStepBundle({ currentTarget }) {
    const row = currentTarget.closest('[data-step-id]');
    const step = this.#step(row.dataset.stepId);
    const index = this.#bundleRows(row).indexOf(currentTarget.closest('[data-role="bundle-row"]'));

    step.bundles.splice(index, 1);

    this.#renderStepBundles(row, step);
    this.#announce();
  }

  addStepMcpServer({ currentTarget }) {
    const row = currentTarget.closest('[data-step-id]');
    const step = this.#step(row.dataset.stepId);

    step.mcpServers.push({ mcpServerId: '', canvasX: null, canvasY: null });

    this.#renderStepMcpServers(row, step);
    this.#announce();
  }

  changeStepMcpServer({ currentTarget }) {
    const row = currentTarget.closest('[data-step-id]');
    const step = this.#step(row.dataset.stepId);

    step.mcpServers = this.#mcpServerRows(row).map((node, index) => {
      const mcpServerId = field(node, 'mcp-server').value;
      const kept = step.mcpServers[index];
      const changed = mcpServerId !== kept?.mcpServerId;

      return {
        mcpServerId,
        canvasX: changed ? null : (kept?.canvasX ?? null),
        canvasY: changed ? null : (kept?.canvasY ?? null),
      };
    });

    this.#renderStepMcpServers(row, step);
    this.#announce(null, { canvas: true });
  }

  deleteStepMcpServer({ currentTarget }) {
    const row = currentTarget.closest('[data-step-id]');
    const step = this.#step(row.dataset.stepId);
    const index = this.#mcpServerRows(row).indexOf(currentTarget.closest('[data-role="mcp-server-row"]'));

    step.mcpServers.splice(index, 1);

    this.#renderStepMcpServers(row, step);
    this.#announce(null, { canvas: true });
  }

  #renderTriggers() {
    const rows = this.#workflow.triggers.map((trigger) => this.#triggerRow(trigger));

    this.triggersListTarget.replaceChildren(...rows);
  }

  #triggerRow(trigger) {
    const node = this.triggerRowTemplateTarget.content.firstElementChild.cloneNode(true);

    node.dataset.triggerId = trigger.id;

    if (trigger.id === this.scene.openTriggerId) node.appendChild(this.#triggerForm(trigger));

    this.#renderTriggerRowHeader(node, trigger);

    return node;
  }

  #renderTriggerRowHeader(node, trigger) {
    const open = trigger.id === this.scene.openTriggerId;
    const faulty = Boolean(this.scene.errors.triggers[trigger.id]);
    const face = triggerFace(trigger, this.#catalog);

    role(node, 'title').textContent = face.title;
    role(node, 'detail').textContent = face.detail;
    role(node, 'delete').hidden = !open;
    this.#tile(node, 'trigger-icon', trigger.kind, this.#catalog.tones.trigger);
    this.#paintRow(node, open, faulty);
  }

  #triggerForm(trigger) {
    const node = this.triggerFieldsTemplateTarget.content.firstElementChild.cloneNode(true);
    const problems = this.scene.errors.triggers[trigger.id] ?? {};

    role(node, 'webhook-fields').hidden = trigger.kind !== 'webhook';
    role(node, 'cron-fields').hidden = trigger.kind !== 'cron';
    field(node, 'name').value = trigger.name;
    field(node, 'webhook').value = trigger.webhookId;
    field(node, 'events').value = trigger.events;
    field(node, 'conditions').value = trigger.conditions;
    field(node, 'cron').value = trigger.cron;
    message(role(node, 'webhook-error'), problems.webhook);
    message(role(node, 'conditions-error'), problems.conditions);
    message(role(node, 'cron-error'), problems.cron);

    return node;
  }

  #renderSteps() {
    const rows = this.#workflow.steps.map((step, index) => this.#row(step, index));

    this.stepsListTarget.replaceChildren(...rows);
  }

  #row(step, index) {
    const node = this.rowTemplateTarget.content.firstElementChild.cloneNode(true);

    node.dataset.stepId = step.id;
    node.dataset.position = index;

    if (step.id === this.scene.openStepId) node.appendChild(this.#fields(step));

    this.#renderRowHeader(node, step);

    return node;
  }

  #renderRowHeader(node, step) {
    const open = step.id === this.scene.openStepId;
    const faulty = Boolean(this.scene.errors.steps[step.id]);

    role(node, 'position').textContent = Number(node.dataset.position) + 1;
    role(node, 'name').textContent = stepTitle(step, this.#catalog);
    role(node, 'detail').textContent = stepDetail(step, this.#catalog);
    this.#tile(node, 'mode-icon', step.mode, this.#catalog.tones.mode);
    this.#paintRow(node, open, faulty);
    this.#deleteButton(node, step, open);
  }

  #paintRow(node, open, faulty) {
    const remove = [...this.rowOpenClasses, ...this.rowIdleClasses, ...this.rowFaultyClasses];

    this.#reveal(role(node, 'warning'), faulty && !open);
    role(node, 'chevron').classList.toggle('-rotate-90', !open);
    role(node, 'toggle').setAttribute('aria-expanded', String(open));

    node.classList.remove(...remove);
    node.classList.add(...(faulty ? this.rowFaultyClasses : open ? this.rowOpenClasses : this.rowIdleClasses));
  }

  #deleteButton(node, step, open) {
    const button = role(node, 'delete');
    const label = this.#catalog.labels.deleteStep.replace('%{name}', stepTitle(step, this.#catalog));

    button.hidden = !open;
    button.title = label;
    button.setAttribute('aria-label', label);
  }

  #fields(step) {
    const node = this.fieldsTemplateTarget.content.firstElementChild.cloneNode(true);
    const problems = this.scene.errors.steps[step.id] ?? {};

    field(node, 'name').value = step.name;
    field(node, 'key').value = step.key;
    field(node, 'command').value = step.command;
    field(node, 'credential_id').value = step.credentialId ?? '';
    field(node, 'prompt').value = step.prompt;
    field(node, 'conditions').value = step.conditions;
    field(node, 'instructions').value = step.instructions ?? '';
    field(node, 'interruptible').checked = Boolean(step.interruptible);
    field(node, 'child_workflow_id').value = step.childWorkflowId ?? '';
    field(node, 'child_inputs').value = step.childInputs ?? '';
    field(node, 'each').value = step.each ?? '';
    field(node, 'allow_failures').checked = Boolean(step.allowFailures);

    for (const problem of ['name', 'key', 'bundles', 'command', 'prompt', 'conditions']) {
      message(role(node, `${problem}-error`), problems[problem]);
    }

    message(role(node, 'mcp-servers-error'), problems.mcpServers);
    message(role(node, 'child-workflow-error'), problems.childWorkflow);
    message(role(node, 'child-inputs-error'), problems.childInputs);
    message(role(node, 'each-error'), problems.each);

    this.#renderStepBundles(node, step);
    this.#renderStepMcpServers(node, step);
    this.#renderChildInputsDeclared(node, step);
    this.#renderMode(node, step);

    return node;
  }

  #renderChildInputsDeclared(node, step) {
    const { labels } = this.#catalog;
    const inputs = callableWorkflow(step.childWorkflowId, this.#catalog)?.inputs ?? [];
    const declared = inputs
      .map((input) => (input.required && !input.defaultValue ? `${input.key}*` : input.key))
      .join(', ');

    role(node, 'child-inputs-declared').textContent = declared
      ? labels.childInputsDeclared.replace('%{inputs}', declared)
      : labels.childInputsFree;
  }

  #renderMode(node, step) {
    for (const button of node.querySelectorAll('[data-role="mode"]')) {
      button.setAttribute('aria-checked', String(button.dataset.mode === step.mode));
      paint(button, button.dataset.mode === step.mode, this.modeActiveClasses, this.modeIdleClasses);
    }

    role(node, 'script-fields').hidden = step.mode !== 'script';
    role(node, 'agent-fields').hidden = step.mode !== 'agent';
    role(node, 'gate-fields').hidden = step.mode !== 'gate';
    role(node, 'workflow-fields').hidden = step.mode !== 'workflow';
    role(node, 'bundles-fields').hidden = step.mode === 'workflow';
  }

  #bundleRows(node) {
    return [...role(node, 'bundles-list').querySelectorAll('[data-role="bundle-row"]')];
  }

  #mcpServerRows(node) {
    return [...role(node, 'mcp-servers-list').querySelectorAll('[data-role="mcp-server-row"]')];
  }

  #renderStepMcpServers(node, step) {
    const rows = step.mcpServers.map((attachment, index) => this.#stepMcpServerRow(step, attachment, index));
    const attached = new Set(step.mcpServers.map((attachment) => attachment.mcpServerId).filter(Boolean));
    const addButton = role(node, 'mcp-server-add');

    role(node, 'mcp-servers-list').replaceChildren(...rows);
    role(node, 'mcp-servers-blank').hidden = step.mcpServers.length > 0;
    role(node, 'mcp-codex-warning').hidden = step.provider !== 'codex' || step.mcpServers.length === 0;

    addButton.disabled = attached.size >= this.#catalog.mcpServers.length;
    addButton.title = addButton.disabled ? this.#catalog.messages.stepMcpServerExhausted : '';
  }

  #stepMcpServerRow(step, attachment, index) {
    const node = this.stepMcpServerTemplateTarget.content.firstElementChild.cloneNode(true);
    const select = field(node, 'mcp-server');
    const taken = new Set(
      step.mcpServers
        .filter((_, otherIndex) => otherIndex !== index)
        .map((other) => other.mcpServerId)
        .filter(Boolean)
    );

    for (const option of [...select.options]) {
      if (option.value && taken.has(option.value)) option.remove();
    }

    select.value = attachment.mcpServerId;

    return node;
  }

  #renderStepBundles(node, step) {
    const rows = step.bundles.map((attachment) => this.#stepBundleRow(attachment));

    role(node, 'bundles-list').replaceChildren(...rows);
    role(node, 'bundles-blank').hidden = step.bundles.length > 0;
  }

  #stepBundleRow(attachment) {
    const node = this.stepBundleTemplateTarget.content.firstElementChild.cloneNode(true);

    field(node, 'bundle').value = attachment.bundleId;
    this.#renderRevisions(node, attachment);
    this.#renderBundleLink(node, attachment);

    return node;
  }

  #revisionsOf(bundleId) {
    return this.#catalog.revisions[bundleId] ?? [];
  }

  #pinnedRevision(attachment) {
    return this.#revisionsOf(attachment.bundleId)
      .find((revision) => revision.number === attachment.revisionNumber);
  }

  #revisionOption(number, value) {
    return new Option(this.#catalog.labels.revisionOption.replace('%{number}', number), value);
  }

  #renderRevisions(node, attachment) {
    const [latest, ...older] = this.#revisionsOf(attachment.bundleId);
    const select = field(node, 'revision');
    const following = latest !== undefined && attachment.revisionNumber !== latest.number;

    if (latest === undefined) select.replaceChildren(new Option(this.#catalog.labels.revisionNone, ''));
    else select.replaceChildren(this.#revisionOption(latest.number, following ? '' : String(latest.number)));

    older.forEach(({ number }) => select.add(this.#revisionOption(number, String(number))));

    select.value = attachment.revisionNumber === null ? '' : String(attachment.revisionNumber ?? '');
    role(node, 'bundle-revision').hidden = !attachment.bundleId;
  }

  #renderBundleLink(node, attachment) {
    const { labels } = this.#catalog;
    const link = role(node, 'bundle-link');
    const revision = this.#pinnedRevision(attachment);
    const label = revision ? labels.openRevision.replace('%{number}', revision.number) : labels.openBundle;

    link.hidden = !attachment.bundleId;
    link.href = this.#bundleHref(attachment.bundleId, revision);
    link.title = label;
    link.setAttribute('aria-label', label);
  }

  #bundleHref(bundleId, revision) {
    if (!bundleId) return '';

    const url = this.#catalog.bundleUrl.replace(':bundle', bundleId);

    return revision ? `${url}?revision=${revision.id}` : url;
  }

  #tile(node, attribute, value, tones) {
    const tile = role(node, 'tile');

    tile.classList.remove(...Object.values(tones).flatMap((tone) => tone.split(' ')));
    tile.classList.add(...tones[value].split(' '));

    for (const icon of tile.querySelectorAll(`[data-${attribute}]`)) {
      this.#reveal(icon, icon.getAttribute(`data-${attribute}`) === value);
    }
  }

  #reveal(node, visible) {
    node.classList.toggle('hidden', !visible);
  }

  #providerOf(id) {
    return this.#catalog.credentials.find((candidate) => candidate.id === id)?.provider ?? '';
  }

  get #catalog() {
    return this.scene.catalog;
  }

  get #workflow() {
    return this.scene.workflow;
  }

  #step(id) {
    return this.#workflow.steps.find((step) => step.id === id);
  }

  #trigger(id) {
    return this.#workflow.triggers.find((trigger) => trigger.id === id);
  }

  #announce(key = null, { canvas = false } = {}) {
    this.dispatch('changed', { detail: { key, canvas } });
  }
}
